// extractRoutineLocals — best-effort scan of a routine body for the names you'd
// reference inside it: stored procedure/function PARAMETERS and local DECLAREd
// variables. Used to offer them in the `@`-completion (even though MySQL params
// aren't @-prefixed) in the routine / form-SP editors. Pure + unit-tested.

// MySQL data-type keywords a DECLARE's name list is followed by — used as a
// lookahead so we only capture real variable declarations (not DECLARE … HANDLER
// / CURSOR / CONDITION, which aren't variables).
const TYPE_KW = [
  'INT', 'INTEGER', 'TINYINT', 'SMALLINT', 'MEDIUMINT', 'BIGINT',
  'DECIMAL', 'DEC', 'NUMERIC', 'FIXED', 'FLOAT', 'DOUBLE', 'REAL', 'BIT',
  'BOOL', 'BOOLEAN', 'SERIAL',
  'DATE', 'DATETIME', 'TIMESTAMP', 'TIME', 'YEAR',
  'CHAR', 'VARCHAR', 'NCHAR', 'NVARCHAR', 'BINARY', 'VARBINARY',
  'BLOB', 'TINYBLOB', 'MEDIUMBLOB', 'LONGBLOB',
  'TEXT', 'TINYTEXT', 'MEDIUMTEXT', 'LONGTEXT', 'ENUM', 'SET', 'JSON',
];
const TYPE_RE = new RegExp(`\\b(?:${TYPE_KW.join('|')})\\b`, 'i');
const DECLARE_RE = new RegExp(
  `\\bDECLARE\\s+((?:\`[^\`]+\`|[\\w$]+)(?:\\s*,\\s*(?:\`[^\`]+\`|[\\w$]+))*)\\s+(?=${TYPE_RE.source})`,
  'gi',
);

function splitTopLevel(s) {
  const out = [];
  let depth = 0, cur = '';
  for (const ch of s) {
    if (ch === '(') { depth++; cur += ch; }
    else if (ch === ')') { depth--; cur += ch; }
    else if (ch === ',' && depth === 0) { out.push(cur); cur = ''; }
    else cur += ch;
  }
  if (cur.trim()) out.push(cur);
  return out;
}

const unquote = (s) => String(s).trim().replace(/^`|`$/g, '');

export function extractRoutineLocals(sql) {
  const text = String(sql || '');
  const found = new Map(); // lower(name) → { name, kind }
  const add = (name, kind) => {
    const n = unquote(name);
    if (/^[\w$]+$/.test(n) && !found.has(n.toLowerCase())) found.set(n.toLowerCase(), { name: n, kind });
  };

  // Parameters: PROCEDURE|FUNCTION <name> ( <param-list> ) — balance the parens.
  const head = /\b(?:PROCEDURE|FUNCTION)\s+(?:`[^`]+`|[\w$]+)\s*\(/i.exec(text);
  if (head) {
    const open = head.index + head[0].length - 1;
    let depth = 0, end = -1;
    for (let i = open; i < text.length; i++) {
      if (text[i] === '(') depth++;
      else if (text[i] === ')' && --depth === 0) { end = i; break; }
    }
    if (end > open) {
      for (const part of splitTopLevel(text.slice(open + 1, end))) {
        // strip a leading IN/OUT/INOUT, then take the first identifier
        const pm = /^\s*(?:(?:IN|OUT|INOUT)\s+)?(`[^`]+`|[\w$]+)/i.exec(part);
        if (pm) add(pm[1], 'param');
      }
    }
  }

  // Local DECLAREs (may declare several names before the type).
  let m;
  while ((m = DECLARE_RE.exec(text))) {
    for (const nm of m[1].split(',')) add(nm, 'local');
  }

  return [...found.values()];
}
